import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { ScamRisk } from './DrawerFacetTypes';
import type { SafetyVerdict, WorkItemSafety } from '../api/workItems';

/** The safe first step for an email the scam check flagged (C29-1). */
export const EMAIL_SAFE_FIRST_STEP = "Don't click links or reply. To check, open the service's app or website yourself.";

/**
 * An email item's safety as the drawer's risk object, while it still blocks: medium or
 * high with no current "Looks safe". A scam verdict keeps high's text-only stop (T2).
 */
export function emailScamRisk(safety: WorkItemSafety | null | undefined): ScamRisk | null {
    if (!safety || safety.verdict === 'safe') return null;
    return {
        level: safety.level,
        reasons: safety.reasons,
        recommended_first_step: EMAIL_SAFE_FIRST_STEP,
    };
}

/**
 * Medium or high scam risk, shown before any action control in the drawer
 * body and never inside a collapsed panel. Safety outranks the closed accordion.
 * Documents read their scam_risk; email items their safety summary, with the two
 * verbs while the person has not decided.
 */
const DrawerRiskAlert: React.FC<{
    scamRisk: ScamRisk | null;
    /** Opens Evidence. Only offered when Evidence has content. */
    onOpenEvidence?: () => void;
    /** C29-1: an email item's safety summary. */
    emailSafety?: WorkItemSafety | null;
    onDecide?: (verdict: SafetyVerdict) => void;
    deciding?: boolean;
}> = ({ scamRisk, onOpenEvidence, emailSafety, onDecide, deciding = false }) => {
    const documentRisk = scamRisk && scamRisk.level !== 'low' ? scamRisk : null;
    const undecidedEmail = !documentRisk && emailSafety && emailSafety.verdict == null ? emailSafety : null;
    if (!documentRisk && !undecidedEmail) return null;

    const level = documentRisk ? documentRisk.level : undecidedEmail!.level;
    const reason = (documentRisk ? documentRisk.reasons[0] : undecidedEmail!.reasons[0])
        || 'This message contains signs of a possible scam.';
    const firstStep = documentRisk ? documentRisk.recommended_first_step : EMAIL_SAFE_FIRST_STEP;
    const showVerbs = Boolean(undecidedEmail && undecidedEmail.decidable && onDecide);

    return (
        <Box sx={{ px: 2, pt: 1.5 }}>
            <Alert
                data-testid="WB_DRAWER_RISK_ALERT"
                severity={level === 'high' ? 'error' : 'warning'}
                variant="outlined"
            >
                <Typography variant="subtitle2">Check this sender before responding</Typography>
                <Typography variant="body2">{reason}</Typography>
                {firstStep && (
                    <Typography variant="body2">{firstStep}</Typography>
                )}
                {documentRisk && onOpenEvidence && (
                    <Button data-testid="WB_TRIAGE_OPEN_EVIDENCE" onClick={onOpenEvidence}
                        sx={{ minHeight: 44, textTransform: 'none' }}>See why</Button>
                )}
                {showVerbs && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        <Button
                            data-testid="WB_RISK_MARK_SAFE"
                            variant="outlined"
                            disabled={deciding}
                            onClick={() => onDecide!('safe')}
                            sx={{ minHeight: 44, textTransform: 'none' }}
                        >
                            Looks safe
                        </Button>
                        <Button
                            data-testid="WB_RISK_MARK_SCAM"
                            variant="outlined"
                            color="error"
                            disabled={deciding}
                            onClick={() => onDecide!('scam')}
                            sx={{ minHeight: 44, textTransform: 'none' }}
                        >
                            It&apos;s a scam
                        </Button>
                    </Box>
                )}
            </Alert>
        </Box>
    );
};

export default DrawerRiskAlert;
